import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  styled,
  IconButton,
  InputBase,
  Checkbox,
  Grid,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { openModal } from '../../redux/slices/profileSlice';
// Hooks & Components
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import Iconify from '../../components/Iconify';
import Page from '../../components/Page';
import palette from '../../theme/palette';

// Assets
import LoginImg from '../../assets/login.png';
import RegisterBanner from '../../assets/registerBanner.png';
import NonRootHeader from '../../layouts/dashboard/header/NonRootHeader';
import PublicFail from '../../assets/ic_public_fail.png';
import Delete from '../../assets/delete.png';
import { ThemeButton } from '../../components/settings/GlobalStyled';

// --- STYLED COMPONENTS ---

const InputContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  borderRadius: '12px',
  height: '36px',
  padding: '0 16px',
  background: '#F6F7F9',
  border: '1px solid transparent',
  transition: 'border 0.2s',
  '&:focus-within': {
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    background: '#FFF',
  },
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  marginLeft: 0, // Remove this if it exists
  paddingLeft: 0, // Ensure no internal padding is pushing the text
  fontSize: '14px',
  fontWeight: 400,
  fontFamily: 'sans-serif',
  flexGrow: 1,
  color: palette.visitor.input.color,
  '& .MuiInputBase-input': {
    paddingLeft: 0, // Target the specific internal input element
    fontSize: 14,
    '&::placeholder': {
      opacity: 1,
      color: palette.visitor.input.placeholder,
    },
  },
}));

const LoginButton = styled(ThemeButton)(({ theme }) => ({
  // Use custom styling if you have to
  height: 36,
  minHeight: 'unset',
}));

const YellowLink = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 400,
  cursor: 'pointer',
  fontSize: 11,
  '&:hover': { textDecoration: 'underline' },
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: '1.4rem',
  color: palette.visitor.dialog.title,
  fontWeight: 600,
}));

const CustomHr = ({ style }) => (
  <hr style={{ borderTop: `1.5px solid ${palette.visitor.dialog.hr}`, borderBottom: 'none', height: 1, ...style }} />
);

// --- MAIN COMPONENT ---

export default function Login() {
  const dispatch = useDispatch();
  const { login } = useAuth();
  const { translate } = useLocales();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  const [formError, setFormError] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!loginForm.username) {
      errors.username = translate('please_enter_correct_username');
    }

    if (!loginForm.password) {
      errors.password = translate('please_enter_correct_password');
    }

    setFormError(errors);

    return Object.keys(errors).length === 0;
  };

  // UPDATED VALIDATION LOGIC
  const isValid = () => {
    let valid = true;
    const _error = {};
    let msg = '';

    // Username Check
    if (!loginForm?.username || loginForm.username.length < 6 || loginForm.username.length > 20) {
      valid = false;
      _error.username = true;
      msg = translate('username_must_be');
    }

    // Password Check
    if (!loginForm?.password || loginForm.password.length < 4 || loginForm.password.length > 20) {
      valid = false;
      _error.password = true;
      // Only set password message if username message isn't already set
      if (!msg) msg = translate('password_must_be');
    }

    if (!valid) {
      setErrorMessage(msg);
    }

    setFormError(_error);
    return valid;
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));

    // Clear error styling for this field as user types
    setFormError((prev) => ({ ...prev, [name]: false }));
  };

  const handleLogin = async () => {
    setErrorMessage(null);
    setFormError({}); // Reset errors before trying

    // Client-side validation check
    if (!isValid()) {
      return;
    }

    setIsLoading(true);
    try {
      await login(loginForm.username, loginForm.password);
      localStorage.removeItem('referralCode');
    } catch (error) {
      // Check if the error message is "Invalid credentials."
      const isInvalidAuth = error.message === 'Invalid credentials.' || !error.success;

      if (isInvalidAuth) {
        setErrorMessage(error.message); // Set "Invalid credentials."
        setFormError({
          username: true, // Turn username field red
          password: true, // Turn password field red
        });
      } else {
        setErrorMessage(error.message || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!isDesktop && <NonRootHeader label={'login'} isClose={true} />}
      <Page
        title="Login"
        sx={{
          margin: 'auto',
          height: { sm: 'calc(100vh - 64px)', xs: 'calc(100% - (64px + 58px))' },
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          // minHeight: {
          //   xs: '100vh',
          //   md: 'calc(100vh - 64px)',
          // },
          m: 0,
          p: 0,
          px: { xs: 'unset', sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1000px',
            mx: 'auto',
            mt: 1,
            // transform: {
            //   xs: 'scale(0.8)',
            //   sm: 'scale(0.9)',
            //   md: 'scale(1)',
            // },
            transformOrigin: 'top center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1000,
              mx: 'auto',
              backgroundColor: { xs: 'unset', sm: 'white' },
              borderRadius: 2,
              overflow: 'hidden',
              height: { xs: 'auto', md: '85vh' }, // Set a definitive height for desktop
              maxHeight: '700px', // Prevent it from becoming too tall on large screens
              display: 'flex',
              boxShadow: isDesktop ? (theme) => theme.customShadows.z24 : 'unset', // Optional: makes it look more "framed"
            }}
          >
            <Grid container alignItems="stretch" sx={{ width: isDesktop ? '100%' : '80%', m: 'auto' }}>
              {/* LEFT SIDE - IMAGE (DESKTOP ONLY) */}
              {isDesktop && (
                <Grid
                  item
                  md={5.5}
                  sx={{
                    height: '100%', // Match parent Box height
                    pt: '0 !important',
                    display: 'flex',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderTopRightRadius: 16,
                      borderBottomRightRadius: 16,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={RegisterBanner}
                      alt="login-banner"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'left center',
                        backgroundColor: '#fff',
                      }}
                    />
                  </Box>
                </Grid>
              )}

              {/* RIGHT SIDE - FORM */}
              <Grid
                item
                xs={12}
                md={6.5}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // px: { xs: 2 },
                  py: 'unset',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    height: { sm: '100%', xs: 'unset' },
                    display: { sm: 'flex', xs: 'unset' },
                    background: { xs: 'unset', sm: '#fff' },
                  }}
                >
                  <Box
                    sx={{
                      p: isDesktop ? 2 : '25px',
                      borderRadius: 1,
                      backgroundColor: 'white',
                      // height: { sm: '100%', xs: 'unset' },
                      width: { sm: '75%', xs: 'unset' },
                      m: 'auto',
                    }}
                  >
                    <img
                      src={LoginImg}
                      alt="login-icon"
                      style={{ width: '30vw', maxWidth: 160, margin: '0 auto 24px', display: 'block' }}
                    />

                    {/* {errorMessage && (
                      <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                        {errorMessage}
                      </Alert>
                    )} */}

                    <Stack spacing={2}>
                      {/* USERNAME */}
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
                          {translate('username')} <span style={{ color: 'red' }}>*</span>
                        </Typography>

                        <InputContainer
                          direction="row"
                          alignItems="center"
                          sx={{
                            bgcolor: formError.username ? '#FFDBD9' : '#F6F7F9',
                          }}
                        >
                          <StyledInput
                            name="username"
                            value={loginForm.username}
                            placeholder={translate('enter_username')}
                            onChange={handleFormChange}
                            sx={{ flex: 1 }}
                          />

                          {/* CSS-based "X" Button */}
                          {formError.username && (
                            <IconButton
                              onClick={() => handleFormChange({ target: { name: 'username', value: '' } })}
                              aria-label="Clear username"
                              size="small" // adjusts padding & icon size
                              sx={{
                                bgcolor: 'transparent',
                                '&:hover': {
                                  bgcolor: '#ff7875', // hover effect if you want color like your original button
                                },
                              }}
                            >
                              <img
                                src={Delete}
                                alt="" // decorative since button has aria-label
                                style={{ width: 20, height: 20 }}
                              />
                            </IconButton>
                          )}
                        </InputContainer>

                        {/* Error Message using a simple circle-bang symbol */}
                        {formError.username && (
                          <Typography
                            sx={{
                              color: '#FB2D60',
                              fontSize: '13px',
                              mt: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <img src={PublicFail} alt="ic_public_fail" style={{ maxWidth: '16px' }} />
                            {translate('please_enter_correct_username')}
                          </Typography>
                        )}
                      </Box>

                      {/* PASSWORD */}
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
                          {translate('password')} <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <InputContainer
                          direction="row"
                          sx={{
                            bgcolor: formError.password ? '#FFDBD9' : '#F6F7F9',
                          }}
                        >
                          <StyledInput
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={translate('enter_password')}
                            value={loginForm.password}
                            onChange={handleFormChange}
                          />
                          <Stack direction="row" alignItems="center">
                            {formError.password ? (
                              <IconButton
                                onClick={() => handleFormChange({ target: { name: 'password', value: '' } })}
                                aria-label="Clear password"
                                size="small"
                                sx={{
                                  bgcolor: 'transparent',
                                  '&:hover': {
                                    bgcolor: '#ff7875',
                                  },
                                }}
                              >
                                <img
                                  src={Delete}
                                  alt="" // decorative since button has aria-label
                                  style={{ width: 20, height: 20 }}
                                />
                              </IconButton>
                            ) : (
                              // 👁 Show Eye when no error
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: '#919EAB', p: 1 }}
                              >
                                <Iconify icon={showPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                              </IconButton>
                            )}
                          </Stack>
                        </InputContainer>
                        {formError.password && (
                          <Typography
                            sx={{
                              color: '#FB2D60',
                              fontSize: '13px',
                              mt: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <img src={PublicFail} alt="ic_public_fail" style={{ maxWidth: '16px' }} />
                            {translate('please_enter_correct_password')}
                          </Typography>
                        )}
                      </Box>

                      {/* REMEMBER & FORGOT */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Checkbox
                            size="small"
                            sx={{ p: 0, '&.Mui-checked': { color: 'primary.main' } }}
                            defaultChecked
                          />
                          <Typography sx={{ fontSize: '11px', fontWeight: 400, color: '#637381' }}>
                            {translate('remember_password')}
                          </Typography>
                        </Stack>
                        <YellowLink
                          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() =>
                            dispatch(
                              openModal({
                                view: 'reset-password',
                              })
                            )
                          }
                        >
                          {translate('forgot_password')}
                        </YellowLink>
                      </Stack>

                      {/* LOGIN BUTTON */}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <LoginButton variant="contained" loading={isLoading} onClick={handleLogin}>
                          {translate('login')}
                        </LoginButton>
                      </Box>

                      {/* REGISTER LINK */}
                      <Typography sx={{ mt: 2, textAlign: 'center', fontSize: '11px', color: '#637381' }}>
                        {translate('dont_you_have_account')}{' '}
                        <YellowLink
                          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => navigate('/auth/register')}
                        >
                          {translate('create_account_now')}
                        </YellowLink>
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Page>
    </>
  );
}
